import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./../Header";
import Footer from "./../Footer";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const RESEND_SECONDS = 60;

export default function ConsultantRegister() {
  const navigate = useNavigate();

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [emailMasked, setEmailMasked] = useState("");
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const validate = () => {
    const newErrors = {};

    if (!phone) newErrors.phone = "手机号不能为空";
    else if (!/^1[3-9]\d{9}$/.test(phone)) newErrors.phone = "手机号格式不正确";

    if (!email) newErrors.email = "邮箱不能为空";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "邮箱格式不正确";
    }

    if (!emailCode.trim()) newErrors.emailCode = "请填写邮箱验证码";

    if (!password) newErrors.password = "密码不能为空";
    else if (password.length < 6) newErrors.password = "密码至少6位";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendEmailCode = async () => {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      setErrors((prev) => ({ ...prev, phone: "请先填写正确的手机号" }));
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrors((prev) => ({ ...prev, email: "请先填写正确的邮箱" }));
      return;
    }
    if (countdown > 0) return;

    setSendingCode(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-email-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role: "consultant",
          purpose: "register",
          email: email.trim(),
          phone: phone.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "验证码发送失败");
        return;
      }
      setEmailMasked(data.email_masked || "");
      setCountdown(RESEND_SECONDS);
      alert(
        data.dev_hint
          ? `${data.message}\n（开发模式：请查看后端控制台验证码）`
          : `验证码已发送到 ${data.email_masked || email.trim()}`
      );
    } catch (err) {
      console.error(err);
      alert("请求失败");
    } finally {
      setSendingCode(false);
    }
  };

  const handleRegister = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/registerconsultant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: phone.trim(),
          email: email.trim(),
          email_code: emailCode.trim(),
          password,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || data.msg || "注册失败");
        return;
      }

      localStorage.setItem("consultant_id", data.consultant_id);
      navigate("/consultant/profile/setup");
    } catch (err) {
      console.error(err);
      alert("请求失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-50 to-indigo-50">
      <Header />

      <div className="flex-1 flex justify-center px-4 mt-10 mb-10">
        <div className="max-w-4xl w-full">
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-800">
            咨询师入驻
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="p-6 rounded-2xl text-white bg-gradient-to-r from-purple-500 to-indigo-500 shadow-lg">
              <h3 className="font-bold mb-2">平台分发</h3>
              <p className="text-sm">AI匹配精准用户</p>
            </div>
            <div className="p-6 rounded-2xl text-white bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg">
              <h3 className="font-bold mb-2">高效变现</h3>
              <p className="text-sm">按咨询收费，稳定收入</p>
            </div>
            <div className="p-6 rounded-2xl text-white bg-gradient-to-r from-purple-400 to-indigo-500 shadow-lg">
              <h3 className="font-bold mb-2">品牌曝光</h3>
              <p className="text-sm">打造个人咨询IP</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow max-w-md mx-auto">
            <h3 className="text-xl font-bold mb-2 text-center">咨询师注册</h3>
            <p className="text-sm text-gray-400 text-center mb-6">
              请填写手机号与常用邮箱，验证码将发送到邮箱
            </p>

            <div className="space-y-5">
              <input
                type="text"
                placeholder="请输入手机号"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setErrors({ ...errors, phone: "" });
                }}
                className={`w-full border p-3 rounded-lg ${
                  errors.phone ? "border-red-500" : ""
                }`}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm -mt-3">{errors.phone}</p>
              )}

              <input
                type="email"
                placeholder="请输入常用邮箱"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors({ ...errors, email: "" });
                }}
                className={`w-full border p-3 rounded-lg ${
                  errors.email ? "border-red-500" : ""
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm -mt-3">{errors.email}</p>
              )}

              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="邮箱验证码"
                  value={emailCode}
                  onChange={(e) => {
                    setEmailCode(e.target.value);
                    setErrors({ ...errors, emailCode: "" });
                  }}
                  className={`flex-1 border p-3 rounded-lg ${
                    errors.emailCode ? "border-red-500" : ""
                  }`}
                />
                <button
                  type="button"
                  onClick={sendEmailCode}
                  disabled={sendingCode || countdown > 0}
                  className="shrink-0 min-w-[120px] rounded-lg bg-purple-500 px-3 py-3 text-sm text-white disabled:opacity-50"
                >
                  {sendingCode
                    ? "发送中…"
                    : countdown > 0
                    ? `${countdown}s`
                    : "获取验证码"}
                </button>
              </div>
              {emailMasked ? (
                <p className="text-xs text-blue-600 -mt-2">
                  验证码已发送至：{emailMasked}
                </p>
              ) : null}
              {errors.emailCode && (
                <p className="text-red-500 text-sm -mt-2">{errors.emailCode}</p>
              )}

              <input
                type="password"
                placeholder="请输入密码（至少6位）"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors({ ...errors, password: "" });
                }}
                className={`w-full border p-3 rounded-lg ${
                  errors.password ? "border-red-500" : ""
                }`}
              />
              {errors.password && (
                <p className="text-red-500 text-sm -mt-3">{errors.password}</p>
              )}

              <button
                onClick={handleRegister}
                disabled={loading}
                className={`w-full py-3 rounded-lg text-white ${
                  loading ? "bg-gray-300" : "bg-purple-500 hover:bg-purple-600"
                }`}
              >
                {loading ? "注册中..." : "立即入驻"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
